import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Divider,
    Link,
    CircularProgress,
    Alert
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { styled } from '@mui/material/styles';
import { uploadMarketplaceFile, downloadMarketplaceTemplate } from '../../services/marketplaceapi';

// Styled component for file input
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

/**
 * Generic file upload modal for different app types
 * 
 * @param {Object} props - Component properties
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {Function} props.onUploadSuccess - Function to call when upload is successful
 * @param {string} props.app - The app type (marketplace, product, etc.)
 * @param {string} props.table - The specific table/entity within the app (amazon, myntra, etc.)
 * @param {string} props.title - Title for the modal
 * @param {Function} props.uploadFunction - Custom function to call to upload the file
 * @param {Function} props.downloadTemplateFunction - Custom function to call to download a template
 * @param {string} props.acceptedFileTypes - File types to accept (.csv, .xlsx, etc.)
 */
const FileUploadModalNew = ({
    open,
    onClose,
    onUploadSuccess,
    app,
    table,
    title = 'Upload Listings',
    uploadFunction = null,
    downloadTemplateFunction = null,
    acceptedFileTypes = '.csv,.xlsx,.xls'
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  const [templateFormat, setTemplateFormat] = useState('xlsx');

    // Default upload functions based on app and table
    const defaultUploadFunctions = {
        marketplace: (formData) => {
            formData.append('marketplace', table);
            return uploadMarketplaceFile(formData);
        }
    };

    // Default template download functions based on app and table
    const defaultTemplateDownloadFunctions = {
        marketplace: (format) => downloadMarketplaceTemplate(table, format)
    };

    // Use provided functions or fall back to defaults
    const handleUploadFile = uploadFunction || defaultUploadFunctions[app] || defaultUploadFunctions.marketplace;
    const handleDownloadTemplateFile = downloadTemplateFunction || defaultTemplateDownloadFunctions[app] || defaultTemplateDownloadFunctions.marketplace;

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Check if file is CSV or Excel
            const validTypes = [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv'
            ];

            if (validTypes.includes(file.type) || file.name.endsWith('.csv') || file.name.endsWith('.xlsx')) {
                setSelectedFile(file);
                setError(null);
            } else {
                setSelectedFile(null);
                setError('Please upload a valid Excel or CSV file');
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file first');
            return;
        }

        setUploading(true);
        setError(null);
        setValidationErrors([]);
        setSuccess(false);        try {
            console.log(`Uploading ${app}/${table} file:`, selectedFile.name);
            
            // Create a FormData object for the file upload
            const formData = new FormData();
            
            // Always append the file with key 'file' - this is what the backend expects
            formData.append('file', selectedFile);
            
            // Log form data entries for debugging
            console.log('FormData contents:');
            for (let [key, value] of formData.entries()) {
                console.log(`- ${key}: ${value instanceof File ? value.name : value}`);
            }

            // Upload file to server using the appropriate function
            const response = await handleUploadFile(formData);
            
            // Check if there are any errors in the response
            if (response?.data?.errors && response.data.errors.length > 0) {
                console.warn(`Upload completed with ${response.data.errors.length} validation warnings`);
            }
            
            // Set success state
            setSuccess(true);
            setSelectedFile(null);
            
            // Notify parent component
            if (onUploadSuccess) {
                onUploadSuccess(response?.data);
            }

            // Close modal after 2 seconds
            setTimeout(() => {
                onClose();
                setSuccess(false);
            }, 2000);
            
        } catch (error) {
            console.error(`Error uploading ${app}/${table}:`, error);
            
            let errorMessage = 'Failed to upload file. Please try again.';
            let validationErrorsList = [];
            
            if (error.response && error.response.data) {
                if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                } else if (error.response.data.detail) {
                    errorMessage = error.response.data.detail;
                } else if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                }
                
                // Check for validation errors array
                if (error.response.data.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
                    validationErrorsList = error.response.data.errors;
                    errorMessage = 'Validation errors found. No changes were made to the database.';
                }
            }
            
            setError(errorMessage);
            setValidationErrors(validationErrorsList);
        } finally {
            setUploading(false);
        }
    };

    const handleDownloadTemplate = async (format = 'xlsx') => {
        try {
            setTemplateFormat(format);
            console.log(`Downloading ${app}/${table} template in ${format} format...`);
            const response = await handleDownloadTemplateFile(format);
            console.log('Response received:', response);

            // Create a blob from the response data with the appropriate MIME type
            const blob = new Blob([response.data], {
                type: format === 'csv' 
                    ? 'text/csv' 
                    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Create a temporary URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary anchor element
            const link = document.createElement('a');
            link.href = url;

            // Set the download attribute with filename from Content-Disposition header
            // or use a default name with the correct extension based on format
            let filename = `${table}_template.${format}`;
            const contentDisposition = response.headers['content-disposition'];

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch && filenameMatch.length > 1) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                    
                    // Ensure the file extension matches the requested format
                    if (!filename.endsWith(`.${format}`)) {
                        const baseName = filename.split('.')[0];
                        filename = `${baseName}.${format}`;
                    }
                    console.log('Filename from header (adjusted):', filename);
                }
            }

            link.setAttribute('download', filename);
            console.log('Downloading as:', filename);

            // Append the link to the body, click it, and then remove it
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Release the blob URL
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading template:', error);
            setError('Failed to download template. Please try again later.');
        }
    };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    setValidationErrors([]);
    setSuccess(false);
    onClose();
  };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{`${title} - ${table.charAt(0).toUpperCase() + table.slice(1)}`}</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box py={2}>
                    <Box
                        sx={{
                            border: '2px dashed #ccc',
                            borderRadius: 2,
                            p: 3,
                            mt: 1,
                            textAlign: 'center',
                            backgroundColor: '#fafafa'
                        }}
                    >
                        {!selectedFile ? (
                            <Box>
                                <Typography variant="body1" gutterBottom>
                                    Drag and drop your file here, or
                                </Typography>
                                <Button
                                    component="label"
                                    variant="contained"
                                    startIcon={<CloudUploadIcon />}
                                    sx={{ mt: 1 }}
                                >
                                    Select File
                                    <VisuallyHiddenInput
                                        type="file"
                                        onChange={handleFileSelect}
                                        accept=".csv,.xlsx,.xls"
                                    />
                                </Button>
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="body1" gutterBottom>
                                    Selected file:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {selectedFile.name}
                                </Typography>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<CloudUploadIcon />}
                                    sx={{ mt: 1 }}
                                >
                                    Change File
                                    <VisuallyHiddenInput
                                        type="file"
                                        onChange={handleFileSelect}
                                        accept=".csv,.xlsx,.xls"
                                    />
                                </Button>
                            </Box>
                        )}
                    </Box>
                    <Typography mt={1} variant="body2" color="text.secondary">
                        Accepted formats: .csv, .xlsx, .xls
                    </Typography>


          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {validationErrors.length > 0 && (
            <Box mt={2} sx={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #f5c6cb', borderRadius: 1, p: 1, bgcolor: '#fff3f3' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="error" gutterBottom>
                Validation Errors:
              </Typography>
              {validationErrors.map((err, index) => (
                <Typography key={index} variant="caption" component="div" color="error.dark" sx={{ mb: 0.5 }}>
                  • {err}
                </Typography>
              ))}
            </Box>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              File uploaded successfully!
            </Alert>
          )}
                </Box>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">

                    {/* Left side - Template download */}
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                        <DownloadIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                        <Box display="flex" alignItems="center" sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            px: 0.5
                        }}>
                            <Link
                                component="button"
                                variant="body2"
                                onClick={() => handleDownloadTemplate('xlsx')}
                                sx={{
                                    px: 1,
                                    py: 0.5,
                                    color: templateFormat === 'xlsx' ? 'primary.main' : 'text.secondary',
                                    fontWeight: templateFormat === 'xlsx' ? 'medium' : 'normal',
                                    backgroundColor: templateFormat === 'xlsx' ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                    borderRadius: '4px',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        backgroundColor: templateFormat === 'xlsx' ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                            >
                                XLSX
                            </Link>
                            <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>|</Typography>
                            <Link
                                component="button"
                                variant="body2"
                                onClick={() => handleDownloadTemplate('csv')}
                                sx={{
                                    px: 1,
                                    py: 0.5,
                                    color: templateFormat === 'csv' ? 'primary.main' : 'text.secondary',
                                    fontWeight: templateFormat === 'csv' ? 'medium' : 'normal',
                                    backgroundColor: templateFormat === 'csv' ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                    borderRadius: '4px',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        backgroundColor: templateFormat === 'csv' ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                            >
                                CSV
                            </Link>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                            template file
                        </Typography>
                    </Box>

                    {/* Right side - Action buttons */}
                    <Box>
                        <Button onClick={handleClose} disabled={uploading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleUpload}
                            disabled={!selectedFile || uploading}
                            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                            sx={{ ml: 1 }}
                        >
                            {uploading ? 'Uploading...' : 'Upload'}
                        </Button>
                    </Box>

                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default FileUploadModalNew;
